// useAuth hook — T056
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { login as apiLogin, logout as apiLogout, refreshToken } from '../services/api';

interface AuthUser {
  userId: string;
  email: string;
  role: 'Admin' | 'User';
}

interface AuthContextType {
  user: AuthUser | null;
  isAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setAuthFromOAuth: (token: string, apiUser: any) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const extractUser = useCallback((token: string): AuthUser | null => {
    const payload = parseJwt(token);
    if (!payload || !payload.userId) return null;
    return { userId: payload.userId, email: payload.email, role: payload.role };
  }, []);

  // On mount, check for existing token
  useEffect(() => {
    const token = localStorage.getItem('cm_token');
    if (token) {
      const payload = parseJwt(token);
      if (payload && payload.exp * 1000 > Date.now()) {
        setUser(extractUser(token));
      } else {
        // Token expired, try refresh
        refreshToken().then((ok) => {
          if (ok) {
            const newToken = localStorage.getItem('cm_token');
            if (newToken) setUser(extractUser(newToken));
          } else {
            localStorage.removeItem('cm_token');
          }
        });
      }
    }
    setIsLoading(false);
  }, [extractUser]);

  // Schedule silent refresh
  useEffect(() => {
    const token = localStorage.getItem('cm_token');
    if (!token || !user) return;

    const payload = parseJwt(token);
    if (!payload?.exp) return;

    const expiresAt = payload.exp * 1000;
    const refreshAt = expiresAt - 60_000; // 60s before expiry
    const delay = refreshAt - Date.now();

    if (delay <= 0) return;

    const timer = setTimeout(async () => {
      const ok = await refreshToken();
      if (ok) {
        const newToken = localStorage.getItem('cm_token');
        if (newToken) setUser(extractUser(newToken));
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [user, extractUser]);

  const loginFn = useCallback(async (email: string, password: string) => {
    const result = await apiLogin(email, password);
    setUser(extractUser(result.token));
  }, [extractUser]);

  const logoutFn = useCallback(() => {
    setUser(null);
    sessionStorage.removeItem('cm_filters');
    apiLogout();
  }, []);

  const setAuthFromOAuth = useCallback((token: string, _apiUser: any) => {
    localStorage.setItem('cm_token', token);
    setUser(extractUser(token));
  }, [extractUser]);

  return (
    <AuthContext.Provider value={{ user, isAdmin: user?.role === 'Admin' || false, isLoading, login: loginFn, logout: logoutFn, setAuthFromOAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
